import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useData } from '../lib/data';
import { READONLY } from '../lib/readonly';
import { useToast } from '../lib/toast';
import { useQuickAdd } from '../lib/quickAdd';
import type { Task, TaskPriority } from '../lib/database.types';
import { ProjectPicker } from './ProjectPicker';

// Global quick-add modal. Opens with `n` (any view) or via the topbar button.
// Defaults the project to last-used (localStorage) or `inbox` so dumping a
// task doesn't require navigating into a project first.
const LAST_PROJECT_KEY = 'tb:lastQuickAddProject';
const INBOX_SLUG = 'inbox';

export function QuickAdd() {
  const { open, close } = useQuickAdd();
  const { upsertTaskLocal, projects } = useData();
  const { push } = useToast();
  const [title, setTitle] = useState('');
  const [projectSlug, setProjectSlug] = useState<string>('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  // Reset + restore last project when modal opens.
  useEffect(() => {
    if (!open) return;
    setTitle('');
    setPriority('medium');
    setError(null);
    const last = typeof window !== 'undefined' ? localStorage.getItem(LAST_PROJECT_KEY) : null;
    const inboxAvailable = projects.some((p) => p.slug === INBOX_SLUG);
    const lastValid = last && projects.some((p) => p.slug === last) ? last : null;
    const startProject =
      lastValid ?? (inboxAvailable ? INBOX_SLUG : projects[0]?.slug ?? '');
    setProjectSlug(startProject);
    setTimeout(() => titleRef.current?.focus(), 10);
  }, [open, projects]);

  // ESC close while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !projectSlug) return;
    if (READONLY) {
      close();
      return;
    }
    setSaving(true);
    setError(null);
    const id = `${projectSlug.slice(0, 3)}-${Math.floor(Date.now() / 1000)}`;
    const { data, error: err } = await supabase
      .from('tb_tasks')
      .insert({
        id,
        project_slug: projectSlug,
        parent_task_id: null,
        title: title.trim(),
        status: 'pending',
        priority,
        due_at: null,
        autonomous: false,
        tags: [],
        blocked_by: [],
        execution_target: 'me',
        description: '',
        done_check: '',
        notes: '',
      })
      .select()
      .single();
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    if (data) {
      upsertTaskLocal(data as Task);
      push({
        kind: 'success',
        title: 'Task added',
        body: title.trim().slice(0, 60),
        ttlMs: 2500,
      });
    }
    if (typeof window !== 'undefined') {
      localStorage.setItem(LAST_PROJECT_KEY, projectSlug);
    }
    close();
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-start pt-[12vh]"
      onClick={close}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="panel shadow-hud w-full max-w-xl mx-4 p-4 space-y-3"
      >
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-mono text-amberHi tracking-widest uppercase">
            quick add
          </div>
          <div className="flex items-center gap-1.5 text-[10px] font-mono text-dim">
            <kbd className="border border-border rounded px-1.5 py-0.5">↵</kbd>
            <span>save</span>
            <span className="text-dim/50 mx-1">·</span>
            <kbd className="border border-border rounded px-1.5 py-0.5">esc</kbd>
            <span>cancel</span>
          </div>
        </div>
        <input
          ref={titleRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="task title…"
          className="w-full px-4 py-3 bg-panelHi border border-border rounded-md
                     text-ink placeholder-dim text-base
                     focus:outline-none focus:border-amber/60 transition-colors"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <ProjectPicker value={projectSlug} onChange={setProjectSlug} projects={projects} />
          <PriorityButtons value={priority} onChange={setPriority} />
        </div>
        <div className="flex items-center justify-end pt-1">
          <button
            type="submit"
            disabled={saving || !title.trim() || !projectSlug}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold
                       bg-amber border border-amber text-bg
                       hover:brightness-110 transition
                       disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
          >
            {saving ? 'adding…' : 'add task'}
          </button>
        </div>
        {error && <div className="text-xs text-red font-mono">{error}</div>}
      </form>
    </div>
  );
}

function PriorityButtons({
  value,
  onChange,
}: {
  value: TaskPriority;
  onChange: (v: TaskPriority) => void;
}) {
  const priorities: TaskPriority[] = ['critical', 'high', 'medium', 'low'];
  return (
    <div className="flex gap-1">
      {priorities.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          className={
            'flex-1 px-2 py-2.5 rounded-md text-xs font-mono uppercase tracking-wider min-h-[44px] transition-colors ' +
            (value === p
              ? `chip chip-${p} opacity-100 !font-bold`
              : 'bg-panelHi border border-border text-dim hover:border-borderHi')
          }
        >
          {p}
        </button>
      ))}
    </div>
  );
}
